<template>
    <div
        v-if="projects.length > 0"
        :class="projects.length === 1 ? 'mb-0' : 'mb-3'"
    >
        <div class="mb-2">
            <span class="font-weight-medium">{{ title }}:</span>
        </div>
        <div class="d-flex flex-wrap ga-2">
            <template v-for="project in projects" :key="project.projectId">
                <!-- Non-accessible projects: tooltip, pointer not-allowed -->
                <v-tooltip
                    v-if="project && !canAccessProject(project.projectId)"
                    location="top"
                    :open-delay="1000"
                >
                    <template #activator="{ props }">
                        <v-chip
                            v-bind="props"
                            class="chip"
                            color="primary"
                            variant="outlined"
                            label
                            style="opacity: 0.7; cursor: not-allowed"
                        >
                            {{ project.name }}
                        </v-chip>
                    </template>
                    <span>
                        You do not have permissions to navigate to this project
                    </span>
                </v-tooltip>

                <!-- Accessible projects: normal clickable chip -->
                <v-chip
                    v-else
                    class="chip"
                    color="primary"
                    variant="outlined"
                    label
                    clickable
                    @click="navigateToProject(project.projectId)"
                >
                    {{ project.name }}
                </v-chip>
            </template>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { canAccessProject as canAccessProjectHelper } from "@/utils/projectUtils";
import { navigateToProjectInfo } from "@/utils/utils";

export default {
    name: "VChipProjectsList",
    props: {
        /**
         * Array of project objects with projectId and name
         * @type {Array<{projectId: string, name: string}>}
         */
        projects: {
            type: Array,
            required: true,
            default: () => [],
        },
        /**
         * Title/label to display above the chips
         * @type {String}
         */
        title: {
            type: String,
            required: true,
        },
    },
    computed: {
        ...mapGetters(["getManagedProjects", "getRights"]),
        /**
         * Checks if the current viewer can access a specific project.
         * - Admins (viewProjects includes "all") can access everything.
         * - Team leads/operators: only projects in getManagedProjects.
         */
        canAccessProject() {
            return (projectId) =>
                canAccessProjectHelper(
                    projectId,
                    this.getRights,
                    this.getManagedProjects
                );
        },
    },
    methods: {
        /**
         * Navigates to the project info page when a chip is clicked
         * @param {string} projectId - The ID of the project to navigate to
         */
        navigateToProject(projectId) {
            if (!projectId) {
                console.error("Project ID is missing", projectId);
                return;
            }
            if (!this.canAccessProject(projectId)) return;
            navigateToProjectInfo(this.$router, projectId);
        },
    },
};
</script>

<style scoped>
:deep(.chip) {
    font-size: 0.875rem;
    line-height: 1.2;
}
</style>
